// Lightweight replacement for the generated `attachSupabaseAuth`.
// Reads the Supabase session directly from localStorage so we do NOT
// pull the @supabase/auth-js SDK into every route bundle (saves ~220 KiB
// unused JS on the home page per PageSpeed Insights).
//
// The Supabase JS client persists its session under the key
// `sb-<projectRef>-auth-token`. We parse only what we need (access_token)
// and never touch the SDK for the "attach bearer header" middleware path.
import { createMiddleware } from "@tanstack/react-start";

const PROJECT_REF =
  import.meta.env.VITE_SUPABASE_PROJECT_ID ??
  (import.meta.env.VITE_SUPABASE_URL
    ? new URL(import.meta.env.VITE_SUPABASE_URL as string).hostname.split(".")[0]
    : "");

const STORAGE_KEY = `sb-${PROJECT_REF}-auth-token`;

function readAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { access_token?: string; expires_at?: number };
    if (!parsed?.access_token) return null;
    // Skip obviously expired tokens; the full SDK will refresh on its own
    // when actual data calls run.
    if (parsed.expires_at && parsed.expires_at * 1000 < Date.now()) return null;
    return parsed.access_token;
  } catch {
    return null;
  }
}

export const attachSupabaseAuth = createMiddleware({ type: "function" }).client(
  async ({ next }) => {
    const token = readAccessToken();
    return next({
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },
);
